import { useEffect, useRef } from 'react';

import { bind } from 'bind-event-listener';

import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { findOverflowScrollParent } from '@atlaskit/editor-common/ui';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import type { AIPlugin } from '../../../aiPluginType';

const SCROLL_MARGIN = 32;

/**
 * Calculate the offset from the editor DOM to the scroll container.
 * Walks up the DOM tree accumulating offsetTop values until reaching the scroll container.
 */
function calculateOffsetTopFromScrollContainer(
	editorDom: Element,
	scrollContainer: HTMLElement,
): number {
	let offset = 0;
	let el: Element | null = editorDom;
	while (el && el !== scrollContainer) {
		if (el instanceof HTMLElement) {
			offset += el.offsetTop;
			el = el.offsetParent;
		} else {
			break;
		}
	}
	return offset;
}

/**
 * Hook to manage auto-scroll behavior during AI streaming.
 *
 * Simplified port of the scroll logic from the original useInEditorDirectStreaming hook.
 * Reads streaming positions from the pure-PM plugin state and automatically scrolls to keep
 * the content visible.
 *
 * Features:
 * - Scrolls to make the start position visible when streaming begins
 * - Auto-scrolls to keep new content visible as it streams in
 * - Respects user scroll: if user manually scrolls, auto-scroll is disabled
 * - Accounts for modal height to prevent content from being hidden behind the AI modal
 *
 * @param options - Hook options
 * @param options.api - The editor plugin API
 * @param options.view - The ProseMirror EditorView
 * @param options.modalRef - Optional ref to the AI modal element (for height calculation)
 */
export const useStreamingScroll = ({
	api,
	view,
	modalRef,
}: {
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	modalRef?: React.RefObject<HTMLDivElement>;
	view: EditorView | undefined;
}) => {
	const scrollParentRef = useRef<HTMLElement | null>(null);
	const hasUserScrolledRef = useRef(false);
	const isProgrammaticScrollRef = useRef(false);

	// Read streaming positions from plugin state
	const { streamStartPos, scrollTargetPos, isStreaming } = useSharedPluginStateWithSelector(
		api,
		['aiExperience'],
		(state) => ({
			streamStartPos: state.aiExperienceState?.streamingState?.streamStartPos,
			scrollTargetPos: state.aiExperienceState?.streamingState?.scrollTargetPos,
			isStreaming: state.aiExperienceState?.isStreaming ?? false,
		}),
	);

	// Find and cache the scroll parent
	useEffect(() => {
		if (!view?.dom) {
			return;
		}

		const scrollParent = findOverflowScrollParent(view.dom);
		if (scrollParent) {
			scrollParentRef.current = scrollParent;
		}

		return () => {
			scrollParentRef.current = null;
		};
	}, [view?.dom]);

	// Set up scroll listener to detect user scrolls
	useEffect(() => {
		const scrollParent = scrollParentRef.current;
		if (!scrollParent) {
			return;
		}

		const handleScroll = () => {
			if (isProgrammaticScrollRef.current) {
				isProgrammaticScrollRef.current = false;
				return;
			}
			// User manually scrolled - disable auto-scroll
			hasUserScrolledRef.current = true;
		};

		const unbind = bind(scrollParent, {
			type: 'scroll',
			listener: handleScroll,
			options: { passive: true },
		});

		return unbind;
	}, [view?.dom]);

	// Perform scroll when streaming positions change
	useEffect(() => {
		const scrollParent = scrollParentRef.current;

		// Reset user scroll flag when streaming starts
		if (isStreaming) {
			hasUserScrolledRef.current = false;
		}

		// Don't auto-scroll if user has manually scrolled
		if (hasUserScrolledRef.current || !scrollParent || !view) {
			return;
		}

		// Skip if no start position to scroll to
		if (streamStartPos === undefined) {
			return;
		}

		try {
			const startCoords = view.coordsAtPos(streamStartPos);
			const offsetTop = view.coordsAtPos(0).top;
			const modalHeight = modalRef?.current?.offsetHeight || 0;
			const offsetTopFromScrollContainer = calculateOffsetTopFromScrollContainer(
				view.dom,
				scrollParent,
			);

			/**
			 * Calculate scroll to show start at top of viewport.
			 * If the start position is above viewport, scroll up.
			 * If below viewport, scroll down.
			 */
			const topScrollTop =
				offsetTopFromScrollContainer + startCoords.top - offsetTop - SCROLL_MARGIN;

			isProgrammaticScrollRef.current = true;

			// If no target position, just scroll to show the start position
			if (scrollTargetPos === undefined) {
				if (scrollParent.scrollTop > topScrollTop) {
					scrollParent.scroll({
						top: topScrollTop,
						behavior: 'instant',
					});
				}
				return;
			}

			// Calculate scroll to show target at bottom of viewport (above modal)
			// Handle edge case where coords could be 0 when at the last position of doc
			let targetBottom = view.coordsAtPos(scrollTargetPos).bottom;
			if (targetBottom === 0 && scrollTargetPos > 0) {
				targetBottom = view.coordsAtPos(scrollTargetPos - 1).bottom;
			}

			const bottomScrollTop =
				offsetTopFromScrollContainer +
				targetBottom -
				offsetTop +
				modalHeight +
				SCROLL_MARGIN -
				scrollParent.offsetHeight;

			if (scrollParent.scrollTop > topScrollTop) {
				// Start scrolled past - scroll up to show start
				scrollParent.scroll({
					top: topScrollTop,
					behavior: 'instant',
				});
			} else if (scrollParent.scrollTop < bottomScrollTop) {
				// Target below viewport - scroll down to show it
				scrollParent.scroll({
					top: bottomScrollTop,
					behavior: 'instant',
				});
			}
		} catch {
			// Ignore scroll errors - positions may be invalid during transitions
		}
	}, [streamStartPos, scrollTargetPos, isStreaming, view, modalRef]);
};
